import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import { useUserContext } from '../contexts/user/UserContext';
import { Client } from '../types/models/Client';
import { ApiResponseClient } from '../types/api/ApiResponseClient';

interface UseClientReturn {
  client: Client | null;
  setClient: React.Dispatch<React.SetStateAction<Client | null>>;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching and managing client/organization data.
 *
 * Reads the authenticated user's clientID from UserContext (hydrated by
 * AuthenticatedApp before any route renders) and fetches GET /clients/{id}.
 * When the user has no clientID yet (pre-onboarding), the hook resolves to
 * client=null with loading=false and error=null — distinct from a fetch
 * failure, so consumers can render their "create your organization" CTA.
 *
 * The effect re-runs when clientID transitions (e.g. null → number after
 * the post-create flow in OrganizationForm), so consumers automatically
 * pick up the new client without a page reload.
 *
 * @returns {UseClientReturn} Object containing:
 *   - client: The client data or null if not found / not yet created
 *   - setClient: Function to manually update client state
 *   - loading: Boolean indicating if the fetch is in progress
 *   - error: Error object if fetch failed, null otherwise
 *   - refetch: Function to manually trigger a refetch
 */
export const useClient = (): UseClientReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const user = useUserContext();
  const clientID = user?.clientID ?? null;

  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState<boolean>(clientID !== null);
  const [error, setError] = useState<Error | null>(null);

  const fetchClient = useCallback(async () => {
    setError(null);

    if (clientID === null) {
      setClient(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await apiFetch(`/clients/${clientID}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseClient<Client>;
      if (data.status === 'success' && data.data?.client) {
        setClient(data.data.client);
      } else {
        setClient(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An error occurred while fetching client data'));
    } finally {
      setLoading(false);
    }
  }, [apiFetch, clientID]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchClient();
    }
  }, [auth.isAuthenticated, fetchClient]);

  return { client, setClient, loading, error, refetch: fetchClient };
};

export default useClient;
